import { useReducer } from 'react'
import { PositionedArrayManager, isFalsyAndNotZero } from '@opallabs/lambda'
import { AllowedEvents, TrackEvent } from '@opallabs/quanta'

export type State = {
  dragging: boolean
  draggedFrom: null | number
  draggedItemId: null | string
  dragZone: null | number
}

export enum ActionType {
  DRAG_START,
  DRAG_ENTER,
  RESET,
}

export type Action =
  | {
      type: ActionType.DRAG_START
      payload: {
        draggedItemId: string
        currentDragZone: number
      }
    }
  | {
      type: ActionType.DRAG_ENTER
      payload: {
        visibleZone: number
      }
    }
  | { type: ActionType.RESET }

interface Item {
  id: string
  position: number
  isNew?: boolean
}

type UpdatePosition<T> = (
  positionedArray: Array<number | null>,
  item: Item & T,
  resetDragState?: () => void
) => void

type DragValues = State & {
  onDragComplete: (e: Drag, track?: TrackEvent) => void
  onDragEnter: React.DragEventHandler<HTMLDivElement>
  onDragOver: (e: Drag) => void
  onDragStart: React.DragEventHandler<HTMLDivElement>
}

export type Drag = React.DragEvent<HTMLDivElement> & {
  target: {
    dataset: {
      dragId: string
      zoneId: string
    }
  }
}

type ReorderItems<T> = {
  list: Array<Item & T>
  draggedItemId: string | null
  dragZone: number | null
  resetDragState: () => void
  updatePosition: UpdatePosition<T>
}

export const initialState: State = {
  dragging: false,
  draggedFrom: null,
  draggedItemId: null,
  dragZone: null,
}

const onDragOver = (e: Drag): void => e.preventDefault()

function reorderItems<T>({
  list,
  draggedItemId,
  dragZone,
  resetDragState,
  updatePosition,
}: ReorderItems<T>) {
  const draggedItem = list.find((p) => p.id === draggedItemId)

  if (dragZone === null || !draggedItem) return null

  const positionedArrayManager = new PositionedArrayManager()
  const unmovedItems = list.filter((p) => p.id !== draggedItemId)
  const positionArray = positionedArrayManager.insertAt(unmovedItems, dragZone)
  const reorderedList = unmovedItems

  if (!positionArray.includes(null)) {
    // all item positions need to be rebalanced
    reorderedList.splice(dragZone, 0, draggedItem)
    reorderedList.forEach((item) => {
      updatePosition(positionArray, item, resetDragState)
    })
  }

  return updatePosition(positionArray, draggedItem, resetDragState)
}

export const reducer = (state: State, action: Action): State => {
  switch (action.type) {
    case ActionType.DRAG_START:
      return {
        ...state,
        dragging: true,
        draggedItemId: action.payload.draggedItemId,
        draggedFrom: action.payload.currentDragZone,
      }
    case ActionType.DRAG_ENTER:
      if (state.dragZone === action.payload.visibleZone) {
        return state
      }
      return { ...state, dragZone: action.payload.visibleZone }
    case ActionType.RESET:
      return initialState
    default:
      throw new Error(
        'action.type must be one of DRAG_START, DRAG_ENTER, or RESET'
      )
  }
}

export function useDraggableList<T>({
  dragImageSource,
  list,
  updatePosition,
}: {
  dragImageSource: string
  list: Array<T & Item>
  updatePosition: UpdatePosition<T>
}): DragValues {
  const [state, dispatch] = useReducer(reducer, initialState)
  const dragImage: HTMLImageElement = new Image()
  dragImage.src = dragImageSource

  const onDragStart = (e: Drag): null | void => {
    if (!e) return null

    const draggedItemId = e.target?.dataset?.dragId
    const currentDragZone = parseInt(e.target?.dataset?.zoneId, 10)

    // using setData is required to make Firefox happy
    e.dataTransfer?.setData('text', '')
    e.dataTransfer?.setDragImage(dragImage, 460, 15)
    if (e.dataTransfer) {
      e.dataTransfer.dropEffect = 'move'
    }
    dispatch({
      type: ActionType.DRAG_START,
      payload: {
        draggedItemId,
        currentDragZone,
      },
    })
  }

  const onDragComplete = (e: Drag, track?: TrackEvent, trackValue?: string) => {
    e.preventDefault()
    const { draggedFrom, dragZone, draggedItemId } = state

    if (isFalsyAndNotZero(dragZone) || dragZone === draggedFrom) {
      return dispatch({ type: ActionType.RESET })
    }

    if (track) {
      track(AllowedEvents.drag, {
        value: trackValue,
      })
    }

    reorderItems<T>({
      list,
      draggedItemId,
      dragZone,
      resetDragState: () => dispatch({ type: ActionType.RESET }),
      updatePosition,
    })
  }

  const onDragEnter = (e: Drag) => {
    if (!e) return null
    e.preventDefault()
    const visibleZone = parseInt(e.target.dataset.zoneId, 10)

    if (!isFalsyAndNotZero(visibleZone)) {
      dispatch({
        type: ActionType.DRAG_ENTER,
        payload: {
          visibleZone,
        },
      })
    }
  }

  return {
    ...state,
    onDragComplete,
    onDragEnter,
    onDragOver,
    onDragStart,
  }
}
